import { t } from '../../services/i18nService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import questionSelectionService from '../../services/QuestionSelectionService';
import uiService from '../../services/UIService';
import responseReader from '../../store/readHelpers/ResponseReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';

const deliveringSubHeaderSelector = {
    selectHeaderForAutoDeliver(noAttemptedQuestions, timeHasExpired, isOnline) {
        if (noAttemptedQuestions) {
            return t('delivering.deliverPageHeaderAutoDeliverUnanswered');
        }

        if (timeHasExpired) {
            if (!isOnline) {
                return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredOffline');
            }
            return t('delivering.deliverPageHeaderAutoDeliverTimeExpired');
        }

        if (!isOnline) {
            return t('delivering.deliverPageHeaderAutoDeliverOffline');
        }

        return t('delivering.deliverPageHeaderAutoDeliver');
    },

    selectHeaderForAutoDeliverWithManual(noAttemptedQuestions, timeHasExpired, isOnline) {
        if (timeHasExpired) {
            if (noAttemptedQuestions) {
                return t('delivering.deliverPageHeaderAutoDeliverWithManualTimeExpiredUnanswered');
            }

            if (!isOnline) {
                return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredOffline');
            }

            return t('delivering.deliverPageHeaderAutoDeliverWithManualTimeExpired');
        }

        if (noAttemptedQuestions) {
            return t('delivering.deliverPageHeaderAutoDeliverWithManualUnanswered');
        }

        if (!isOnline) {
            return t('delivering.deliverPageHeaderAutoDeliverOffline');
        }

        return t('delivering.deliverPageHeaderAutoDeliverWithManual');
    },

    selectDefaultHeaderSubCaption(unAttemptedQuestions, numberOfQuestions, autoDeliveringEnabled, timeHasExpired) {
        if (autoDeliveringEnabled && timeHasExpired) {
            return unAttemptedQuestions === numberOfQuestions
                ? t('delivering.statusTimeExpiredUnanswered')
                : t('delivering.statusTimeExpired');
        }

        if (unAttemptedQuestions) {
            const sectionManualSelectionEnabled = marketplacePropertiesService.isSectionManualSelectionV2Enabled();
            if (sectionManualSelectionEnabled && questionSelectionService.areAllSectionsWithQuestionSelectionEnabled()) {
                return t('delivering.statusCandidateSelectionIncomplete', { number: unAttemptedQuestions });
            }

            if (questionSelectionService.atLeastOneSectionWithQuestionSelectionEnabled()) {
                return sectionManualSelectionEnabled
                    ? t('delivering.statusCandidateOnlySelected')
                    : t('delivering.statusCandidateSelectionIncomplete', { number: unAttemptedQuestions });
            }

            return t('delivering.statusIncomplete', { number: unAttemptedQuestions });
        }

        return t('delivering.statusCompleted');
    },

    selectDefaultUnattemptedSubCaption(unAttemptedQuestions) {
        if (!marketplacePropertiesService.isSectionManualSelectionV2Enabled()) {
            return null;
        }

        const mixedQuestionSelectionTypes = questionSelectionService.hasMixedQuestionSelectionTypeSections();
        if (!mixedQuestionSelectionTypes || !unAttemptedQuestions) {
            return null;
        }

        return t('delivering.statusCandidateTotalUnattempted', {
            total: unAttemptedQuestions,
            selfSelected: questionSelectionService.getMissedSelectedQuestionAmount(),
            preSelected: questionSelectionService.getUnattemptedPreselectedQuestions().length,
        });
    },

    selectDefaultHeaderCaption(noAttemptedQuestions, autoDeliveringEnabled, isOnline, timeHasExpired) {
        if (autoDeliveringEnabled) {
            return sessionReader.isAutoDeliveringWithManualEnabled()
                ? this.selectHeaderForAutoDeliverWithManual(noAttemptedQuestions, timeHasExpired, isOnline)
                : this.selectHeaderForAutoDeliver(noAttemptedQuestions, timeHasExpired, isOnline);
        }

        return sessionReader.isPublicExam()
            ? ''
            : t('delivering.deliverPageHeader');
    },

    selectSimplifiedHeaderCaption() {
        return settingsReader.isRedirectUrlEnabled() && !!settingsReader.getRedirectUrl()
            ? t('testOverview.goToRedirectionInstructions')
            : t('testOverview.startOverTextInstructions');
    },

    selectUnAttemptedQuestions(numberOfQuestions) {
        const numberOfResponses = responseReader.getTotalNumberOfResponses();
        const totalSelectedQuestionsToPull = questionSelectionService.getTotalSelectionQuestionsToPull();
        const notExcludedAttemptedQuestions = questionSelectionService.getNotExcludedAttemptedSelectionQuestions().length;
        const allSelectionQuestions = questionSelectionService.getAllSelectionQuestions().length;
        const attemptedSelectionQuestions = questionSelectionService.getAttemptedSelectionQuestions().length;

        const unAttemptedQuestions = (numberOfQuestions + attemptedSelectionQuestions + totalSelectedQuestionsToPull)
            - (allSelectionQuestions + numberOfResponses + notExcludedAttemptedQuestions);

        return unAttemptedQuestions;
    },

    selectProps(ownProps) {
        const {
            numberOfQuestions,
            timeHasExpired,
            apiState: isOnline,
        } = ownProps;

        const unAttemptedQuestions = this.selectUnAttemptedQuestions(numberOfQuestions);
        const noAttemptedQuestions = unAttemptedQuestions === numberOfQuestions;
        const autoDeliveringEnabled = sessionReader.isAutoDeliveringEnabled();
        const hasSelectQuestions = questionSelectionService.atLeastOneSectionWithQuestionSelectionEnabled();

        const defaultHeaderSubCaption = this.selectDefaultHeaderSubCaption(unAttemptedQuestions, hasSelectQuestions, numberOfQuestions,
            autoDeliveringEnabled, timeHasExpired);

        return {
            defaultHeaderCaption: this.selectDefaultHeaderCaption(noAttemptedQuestions, autoDeliveringEnabled, isOnline, timeHasExpired),
            defaultHeaderSubCaption,
            defaultUnattemptedSubCaption: this.selectDefaultUnattemptedSubCaption(unAttemptedQuestions),
            noAttemptedQuestions,
            shouldFocusHeader: !marketplacePropertiesService.isSimplifiedSummaryPageForPublicTestEnabled(),
            showSimplifiedVersionForPublicTests: uiService.shouldUseSimplifiedSummaryPage(),
            simplifiedHeaderCaption: this.selectSimplifiedHeaderCaption(),
        };
    },
};

export default deliveringSubHeaderSelector;
